import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Breadcrumbs, Typography, Switch, TextField, FormControlLabel,
    FormControl, InputLabel, OutlinedInput, InputAdornment, IconButton, FormHelperText,
    Tabs, Tab,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { createUser, getById } from '../../../../services/usersService';
import { useToaster } from '../../../../providers/ToasterProvider';
import { ProjectContext } from '../../../../providers/projectProvider';
import patterns from '../../../../constant/patterns';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import RadiusButton from '../../../../components/RadiusButton';
import { UserCreationMail } from '../UserCreationMail/UserCreationMail';
import { EvaluatorProfilePage } from './EvaluatorProfilePage';
import { EvaluatorSectionsPage } from './EvaluatorSectionsPage';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import EYE_ICON from '../../../../assets/images/eye.svg';
import EYE_CLOSE_ICON from '../../../../assets/images/eye-close.svg';
import BACK from '../../../../assets/images/back.svg';

import './EvaluatorDetails.scss';

// ─── Create-mode schema ───────────────────────────────────────────────────────

const createSchema = z.object({
    firstName: z.string().min(1, { message: 'First name is required.' }),
    lastName: z.string().min(1, { message: 'Last name is required.' }),
    username: z.string().min(1, { message: 'User name is required.' }),
    email: z.string().min(1, { message: 'Email id is required.' }).email('Please enter valid email address.'),
    password: z.string(),
}).superRefine(({ username, password }, ctx) => {
    if (username.length < 8 || username.length > 20) {
        ctx.addIssue({ code: 'custom', message: 'Username should be at least 8 to 20 characters long', path: ['username'] });
    }
    if (!patterns.USERNAME.test(username)) {
        ctx.addIssue({ code: 'custom', message: 'Username must contain only alphanumeric characters, underscores, periods, or hyphens', path: ['username'] });
    }
    if (password.length < 8) {
        ctx.addIssue({ code: 'custom', message: 'Password should be at least 8 characters long', path: ['password'] });
    }
    if (!patterns.PASSWORD.test(password)) {
        ctx.addIssue({ code: 'custom', message: 'Password must contain one uppercase, one lowercase, one digit and one special character', path: ['password'] });
    }
});

// ─── Component ────────────────────────────────────────────────────────────────

export const EvaluatorDetails = () => {
    const params = useParams();
    const projectName = params?.projectName;
    const location = useLocation();
    const navigate = useNavigate();
    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);

    // ── Shared state (used by shell + passed to page components) ──────────
    const [editMode, setEditMode] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [mailModal, setMailModal] = useState({ isOpen: false, isUpdated: false, cancelPressed: false });
    const [activeTab, setActiveTab] = useState(0);

    // ── Create-mode only ──────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);

    const {
        register: registerCreate,
        handleSubmit: handleSubmitCreate,
        formState: { errors: createErrors },
        trigger: triggerCreate,
        reset: resetCreate,
    } = useForm({ resolver: zodResolver(createSchema) });

    // ─── Load user for edit mode ──────────────────────────────────────────

    useEffect(() => {
        const userId = location?.state?.userId;
        if (!userId) return;
        getById(userId)
            .then((res) => {
                if (res.code === 'OK') {
                    setEditMode(true);
                    setIsActive(res.data.isActive);
                    setUserDetails(res.data);
                }
            })
            .catch((err) => console.log('Error fetching evaluator details', err));
    }, [location]);

    useEffect(() => {
        if (mailModal.cancelPressed) navigate(`/${projectName}/evaluator`);
    }, [mailModal, navigate, projectName]);

    // ─── Handlers ────────────────────────────────────────────────────────

    const onGoBack = () => { if (projectName) navigate(`/${projectName}/evaluator`); };

    const onCreateSubmit = async (values) => {
        setIsLoading(true);
        try {
            const res = await createUser({
                firstName: values.firstName,
                lastName: values.lastName,
                username: values.username,
                email: values.email,
                password: values.password,
                isActive,
                userType: 10,
            });
            if (res?.code === 'CREATED') {
                setUserDetails({ ...res.data.user, password: values.password });
                setMailModal({ ...mailModal, isOpen: true });
                addNotification('Evaluator created successfully', 'success');
                resetCreate();
            }
        } catch (err) {
            if (err?.response?.data?.data?.code === 'E_DUPLICATE') addNotification('User name is taken.', 'danger');
            console.log('Error creating evaluator', err);
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────

    return (
        <MainContainer sx={{ bgcolor: 'red' }}>
            <Menu>
                <Breadcrumbs aria-label='breadcrumb' separator='' sx={{ gap: 12 }}>
                    <Typography color='text.primary' sx={{ fontWeight: 400, textTransform: 'capitalize' }}>
                        {projectDetails?.projectName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}><img src={ARROW} alt='' /></Box>
                    <Typography color='text.primary' sx={{ fontWeight: 500 }}>Evaluator</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}><img src={ARROW_OPEN} alt='' /></Box>
                </Breadcrumbs>
            </Menu>

            <Container sx={{ display: 'flex', flexDirection: 'column' }}>
                <div className='search-header-wrapper' style={{ marginBottom: '24px' }}>
                    <Typography component='h5' variant='h5' sx={{ mr: 'auto' }}>
                        {editMode ? 'Edit' : 'Create'} Evaluator User
                    </Typography>
                </div>

                {/* ════════════════════════════════════════
                    CREATE MODE — single form
                    ════════════════════════════════════════ */}
                {!editMode && (
                    <Box sx={{ bgcolor: 'background.base', borderRadius: 6, p: 4 }} className='create-user-form-wrapper'>
                        <Box component='form' autoComplete='off' onSubmit={handleSubmitCreate(onCreateSubmit)}>
                            <div className='user-form-details'>

                                <TextField id='firstName' name='firstName' className='form-field'
                                    label={<><Box component='span'>First Name</Box><Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                    variant='outlined' {...registerCreate('firstName')}
                                    error={Boolean(createErrors.firstName)} helperText={createErrors.firstName?.message}
                                    onBlur={() => triggerCreate('firstName')} />

                                <TextField id='lastName' name='lastName' className='form-field'
                                    label={<><Box component='span'>Last Name</Box><Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                    variant='outlined' {...registerCreate('lastName')}
                                    error={Boolean(createErrors.lastName)} helperText={createErrors.lastName?.message}
                                    onBlur={() => triggerCreate('lastName')} />

                                <TextField id='username' name='username' className='form-field'
                                    label={<><Box component='span'>User Name</Box><Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                    variant='outlined' {...registerCreate('username')}
                                    error={Boolean(createErrors.username)} helperText={createErrors.username?.message}
                                    onBlur={() => triggerCreate('username')} />

                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControl variant='outlined' sx={{ mb: 2 }}>
                                        <InputLabel htmlFor='pwCreate'>
                                            <Box component='span'>Password</Box><Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                        </InputLabel>
                                        <OutlinedInput id='pwCreate' type={passwordVisible ? 'text' : 'password'}
                                            endAdornment={
                                                <InputAdornment position='end'>
                                                    <IconButton onClick={() => setPasswordVisible(!passwordVisible)} edge='end'>
                                                        <img className='password-eye' src={passwordVisible ? EYE_CLOSE_ICON : EYE_ICON} alt='' />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label='Password' {...registerCreate('password')}
                                            error={Boolean(createErrors.password)} onBlur={() => triggerCreate('password')} />
                                        {createErrors.password?.message && (
                                            <FormHelperText sx={{ color: 'error.main' }}>{createErrors.password.message}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Box>

                                <TextField id='email' name='email' className='form-field'
                                    label={<><Box component='span'>Email</Box><Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                                    variant='outlined' {...registerCreate('email')}
                                    error={Boolean(createErrors.email)} helperText={createErrors.email?.message}
                                    onBlur={() => triggerCreate('email')} />

                            </div>

                            <FormControlLabel sx={{ mb: 3 }}
                                control={<Switch checked={isActive} onChange={() => setIsActive(!isActive)} />}
                                label={<Box component='span'>Active</Box>} />

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <RadiusButton sx={{ textTransform: 'uppercase', fontWeight: 500 }} color='grey'
                                    variant='contained' size='large' onClick={onGoBack} startIcon={<img src={BACK} alt='' />}>
                                    Back
                                </RadiusButton>
                                <LoadingButton sx={{ textTransform: 'uppercase', borderRadius: 40 }}
                                    color='primary' type='submit' variant='contained' size='large' loading={isLoading}>
                                    Submit
                                </LoadingButton>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* ════════════════════════════════════════
                    EDIT MODE — tab shell + page components
                    ════════════════════════════════════════ */}
                {editMode && (
                    <Box sx={{ bgcolor: 'background.base', borderRadius: 6}}>

                        {/* Tab bar */}
                        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 4, pt: 2 }}>
                            <Tabs
                                value={activeTab}
                                onChange={(_, v) => setActiveTab(v)}
                                sx={{
                                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '15px', minWidth: 120 },
                                    '& .Mui-selected': { color: 'primary.main' },
                                    '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                                }}
                            >
                                <Tab label='Profile' />
                                <Tab label='Sections' />
                            </Tabs>
                        </Box>

                        {/* Tab pages — each is its own component with isolated state */}
                        {activeTab === 0 && (
                            <EvaluatorProfilePage
                                userDetails={userDetails}
                                setUserDetails={setUserDetails}
                                isActive={isActive}
                                setIsActive={setIsActive}
                                mailModal={mailModal}
                                setMailModal={setMailModal}
                                onGoBack={onGoBack}
                            />
                        )}
                        {activeTab === 1 && (
                            <EvaluatorSectionsPage userDetails={userDetails} />
                        )}

                    </Box>
                )}

            </Container>

            <UserCreationMail
                userDetails={userDetails}
                openState={mailModal.isOpen}
                isUpdated={mailModal.isUpdated}
                setMailModal={setMailModal}
            />
        </MainContainer>
    );
};
